import type {
  BackupManifest,
  LocalAnalyticsEvent,
  LocalCompletion,
  LocalDocument,
  LocalDraft,
  LocalSignature,
  MyInfo,
  StorageDestination,
  Template,
  Workflow,
} from '@signbento/contracts'
import type { VaultPayloadEnvelope } from '@signbento/crypto'

/**
 * Local vault contract — PRD §8.5, §12.1, §12.6 (LOC-02/03/06/10).
 * Records are encrypted payloads with a monotonically increasing revision;
 * blobs are immutable, content-addressed-by-key byte artifacts. Writes are
 * committed revisions; nothing here touches the network or requires an account.
 */

export type VaultRecordKind =
  | 'document'
  | 'draft'
  | 'template'
  | 'workflow'
  | 'signature'
  | 'completion'
  | 'my_info'
  | 'setting'
  | 'analytics_event'
  | 'backup_manifest'
  | 'storage_destination'

export interface StoredRecord {
  readonly key: string
  readonly kind: VaultRecordKind
  readonly id: string
  /** Starts at 1 and increases by exactly one per committed write. */
  readonly revision: number
  readonly committed_at: string
  /** Content-free local ID used only to seek completion records without decrypting the corpus. */
  readonly completion_document_id?: string
  readonly payload: VaultPayloadEnvelope
}

export type RevisionCondition =
  | { readonly kind: 'any' }
  | { readonly kind: 'must_not_exist' }
  | { readonly kind: 'expected'; readonly revision: number }

export type ExpectedRevision = number | 'new'

export interface CommitOptions {
  /**
   * Optimistic concurrency token. Omit for last-write-wins (settings-like
   * records); pass `'new'` for create-only writes and the last committed
   * revision for autosave/edits. A mismatch fails with
   * OP_STORAGE_REVISION_CONFLICT and the current revision.
   */
  readonly expected_revision?: ExpectedRevision
}

export interface CommitResult {
  readonly revision: number
  readonly committed_at: string
}

export interface BlobRef {
  readonly key: string
  readonly byte_length: number
  readonly sha256: string
}

export interface VaultChange {
  readonly kind: VaultRecordKind
  readonly id: string
  readonly revision: number
  readonly committed_at: string
  readonly deleted: boolean
}

/** Two-tab conflict detection: revision checks decide; this notifies peers. */
export interface VaultChangeChannel {
  publish(change: VaultChange): void
  subscribe(listener: (change: VaultChange) => void): () => void
  close(): void
}

export interface VaultClock {
  now(): Date
}

export interface SettingEntry {
  readonly key: string
  readonly value: unknown
}

/**
 * A bounded document read (S6.4/PER-01). With no options the vault returns every document —
 * the original behaviour. With a `limit` it returns that many, starting at `offset`, in the
 * vault's own list order, and reads no further: the documents outside the slice are never
 * decrypted. A surface that shows a screenful of a 10,000-record corpus therefore pays for a
 * screenful, not for the corpus.
 */
export interface DocumentListOptions {
  readonly limit?: number
  readonly offset?: number
}

export interface LocalVault {
  putDocument(record: LocalDocument, options?: CommitOptions): Promise<CommitResult>
  getDocument(id: string): Promise<LocalDocument | null>
  listDocuments(options?: DocumentListOptions): Promise<LocalDocument[]>
  /** How many documents the vault holds, without reading one of them. */
  countDocuments(): Promise<number>
  deleteDocument(id: string, options?: CommitOptions): Promise<void>

  putDraft(record: LocalDraft, options?: CommitOptions): Promise<CommitResult>
  getDraft(id: string): Promise<LocalDraft | null>
  /** Newest drafts first; a bounded read decrypts only the requested slice. */
  listDrafts(options?: DocumentListOptions): Promise<LocalDraft[]>
  deleteDraft(id: string, options?: CommitOptions): Promise<void>

  putTemplate(record: Template, options?: CommitOptions): Promise<CommitResult>
  getTemplate(id: string): Promise<Template | null>
  listTemplates(): Promise<Template[]>
  deleteTemplate(id: string, options?: CommitOptions): Promise<void>

  putWorkflow(record: Workflow, options?: CommitOptions): Promise<CommitResult>
  getWorkflow(id: string): Promise<Workflow | null>
  listWorkflows(): Promise<Workflow[]>
  deleteWorkflow(id: string, options?: CommitOptions): Promise<void>

  putSignature(record: LocalSignature, options?: CommitOptions): Promise<CommitResult>
  getSignature(id: string): Promise<LocalSignature | null>
  listSignatures(): Promise<LocalSignature[]>
  deleteSignature(id: string, options?: CommitOptions): Promise<void>

  putCompletion(record: LocalCompletion, options?: CommitOptions): Promise<CommitResult>
  getCompletion(id: string): Promise<LocalCompletion | null>
  listCompletions(options?: DocumentListOptions): Promise<LocalCompletion[]>
  /** Latest completion for one source document; legacy vaults build the lookup once. */
  latestCompletionForDocument(documentId: string): Promise<LocalCompletion | null>
  deleteCompletion(id: string, options?: CommitOptions): Promise<void>

  putMyInfo(record: MyInfo, options?: CommitOptions): Promise<CommitResult>
  getMyInfo(): Promise<MyInfo | null>

  putSetting(key: string, value: unknown, options?: CommitOptions): Promise<CommitResult>
  getSetting<T = unknown>(key: string): Promise<T | null>
  listSettings(): Promise<SettingEntry[]>

  appendAnalyticsEvent(event: LocalAnalyticsEvent): Promise<CommitResult>
  listAnalyticsEvents(): Promise<LocalAnalyticsEvent[]>

  putBackupManifest(record: BackupManifest, options?: CommitOptions): Promise<CommitResult>
  getBackupManifest(): Promise<BackupManifest | null>

  putStorageDestination(record: StorageDestination, options?: CommitOptions): Promise<CommitResult>
  getStorageDestination(id: string): Promise<StorageDestination | null>
  listStorageDestinations(): Promise<StorageDestination[]>
  deleteStorageDestination(id: string, options?: CommitOptions): Promise<void>

  /** Current committed revision for optimistic concurrency, or null when absent. */
  getRecordRevision(kind: VaultRecordKind, id: string): Promise<number | null>

  /** Immutable; a key that already exists cannot be overwritten. */
  putBlob(key: string, bytes: Uint8Array): Promise<BlobRef>
  getBlob(key: string): Promise<Uint8Array>
  hasBlob(key: string): Promise<boolean>
  deleteBlob(key: string): Promise<void>
  listBlobKeys(): Promise<string[]>
}

export const MY_INFO_RECORD_ID = 'singleton'
export const BACKUP_MANIFEST_RECORD_ID = 'current'

export function recordKey(kind: VaultRecordKind, id: string): string {
  return `${kind}:${id}`
}

/**
 * The vault's one list order: newest commit first, then the record key descending.
 *
 * It is a pure function of two stored fields so that every path can express it — the full
 * `list` sorts with it, the bounded `query` is *defined* by it, and the IndexedDB adapter's
 * `by-kind-committed` index reproduces it (a `prev` cursor over `(kind, committed_at)` yields
 * committed_at descending and, inside one commit, primary-key descending). The tie-break is a
 * code-unit comparison of the key rather than `localeCompare`, because that is the comparison
 * IndexedDB itself applies to its keys; a locale-aware tie-break could not be expressed as an
 * index order and the bounded read would silently disagree with the full one.
 */
export function compareRecordsForListing(left: StoredRecord, right: StoredRecord): number {
  if (left.committed_at !== right.committed_at)
    return left.committed_at < right.committed_at ? 1 : -1
  if (left.key === right.key) return 0
  return left.key < right.key ? 1 : -1
}

export function revisionCondition(options?: CommitOptions): RevisionCondition {
  if (options?.expected_revision === undefined) return { kind: 'any' }
  if (options.expected_revision === 'new') return { kind: 'must_not_exist' }
  return { kind: 'expected', revision: options.expected_revision }
}
